
import { omit, pick, areStringsInsensitivelyEqual, getOrCreate } from 'shared/utils/utils'
import { Type, isAdminToolFlowstep } from 'shared/utils/flowstep';
import * as QA from 'shared/utils/queryAnalysis';


export const Source = {
  Mapping:  1 << 0,
  Constant: 1 << 1,
  get All() { return this.Constant | this.Mapping },
};

export const ErrorType = {
  InvalidUpsert: 'upsert',
  InvalidUpdate: 'update',
  FieldIsEmpty: 'field-is-empty',
  FieldIsReused: 'field-is-reused',
  EmptyMapping: 'empty-mapping',
  HeadersCountMissmatch: 'headers-mismatch',
  ValueError: 'value-error',
  UnconfirmedMapping: 'unconfirmed-mapping',
};


export class UsageMap {
  names = new Map();

  clone() {
    const copy = new UsageMap();
    for (const [key, records] of this.names.entries()) {
      copy.names.set(key, [...records]);
    }
    return copy;
  }

  add(item) {
    // this.removeById(item.id);
    const { name } = item;
    const records = this.getNameRecords(name);
    records.push(item);
  }

  remove(item) {
    const { name, id } = item;
    const records = this.getNameRecords(name);
    const index = records.findIndex(x => x.id === id);
    if (index !== -1) {
      records.splice(index, 1);
    }
  }

  removeById(id) {
    for (const records of this.names.values()) {
      const index = records.findIndex(x => x.id === id);
      if (index !== -1) {
        records.splice(index, 1);
      }
    }
  }

  clear() {
    this.names.clear();
  }

  removeAllFromSource(type) {
    for (const [key, records] of this.names.entries()) {
      const filtered = records.filter(x => x.type !== type);
      if (filtered.length !== records.length) {
        this.names.set(key, filtered);
      }
    }
  }

  checkUsage({ id, name }, filterFn) {
    const field = this.getFirst(name, filterFn);
    return (!field || field.id === id) ? null : field;
  }

  getNameRecords(name) {
    return getOrCreate(this.names, QA.toCanonicalForm(name), () => []);
  }

  hasName(name, filterFn) {
    return !!this.getFirst(name, filterFn);
  }

  getFirst(name, filterFn) {
    const fields = this.getNameRecords(name);
    return this.findFirstNonMasked(fields, filterFn);
  }

  isEmpty(filterFn) {
    for (const v of this.names.values()) {
      if (this.findFirstNonMasked(v, filterFn)) { return false; }
    }
    return true;
  }

  findFirstNonMasked(records, filterFn) {
    return records.find(x => !filterFn || filterFn(x));
  }
};


export class ValidationResult {
  bySourceId = new Map();
  general = [];
  mapping = [];
  constants = [];
  hasAnyError = false;

  static fromCtx(ctx, toValidate) {
    const vr = new ValidationResult();
    const res = validateWholeMapping(ctx, toValidate);
    
    const toSourceId = (error) => error.sourceId && getOrCreate(vr.bySourceId, error.sourceId, () => []).push(error);
    res.constants.forEach(toSourceId);
    res.mapping.forEach(toSourceId);
    
    Object.assign(vr, res);
    return vr;
  }

  getFieldStatus(field) {
    // Handle the case when 'Id' field is assigned to several headers, but only one is currently enabled.
    // In this case we have to gray out other headers
    const isNotFirstIdField = (this.firstIdFieldId && this.firstIdFieldId !== field.id);
    return {
      isDisabled: (this.isActiveField && !this.isActiveField(field)) || isNotFirstIdField,
      errors: this.bySourceId.get(field.id) ?? [],
    };
  }
};


function validateWholeMapping(ctx, toValidate = Source.All) {
  const { usedFields, flowstepType, isConfirmedMapping, excelHeadersToIgnore } = ctx;
  
  const isNotFieldToIgnore = (record) => !record.excelHeader || !excelHeadersToIgnore.has(record.excelHeader.index);
  const firstIdField = usedFields.getFirst(QA.KnownFields.Id, isNotFieldToIgnore);
  const hasIdField = !!firstIdField;
  const ignoreAllExceptId = flowstepType === Type.ConvertLeads && hasIdField;
  const firstIdFieldId = ignoreAllExceptId ? firstIdField.id : null;
  const isActiveField = (field) => {
    return isNotFieldToIgnore(field) && (!ignoreAllExceptId || QA.toCanonicalForm(field.fieldData?.name ?? '') === QA.KnownFields.Id);
  }

  const hasNoFields = usedFields.isEmpty(isActiveField);
  const allowEmptyMapping = isAdminToolFlowstep(flowstepType);
  // We can skip check when there are no used fields or constants at all. So, even if excelHeadersToIgnore are changed
  // it doesn't influence the mapping in any way. That's is why isEmpty is called wihtout any filter function
  const canConfimationBeSkipped = allowEmptyMapping && usedFields.isEmpty(); // don't use 'hasNoFields' here
  
  let general = [], constants = [], mapping = [];

  if (!canConfimationBeSkipped && !isConfirmedMapping) {
    general.push({
      message: `Confirm mapping parameters to continue`,
      type: ErrorType.UnconfirmedMapping,
    });
  }
  else {
    hasNoFields && !allowEmptyMapping && general.push({ 
      message: `None of mappings is enabled and no constants are used`, 
      type: ErrorType.EmptyMapping 
    });

    if (flowstepType === Type.Update) {
      !hasIdField && general.push({ 
        message: `Field '${QA.KnownFields.Id}' must either participate in mapping or be a constant`, 
        type: ErrorType.InvalidUpdate,
      });
    }

    const ctx2 = { ...ctx, isActiveField };

    if (toValidate & Source.Constant) {
      constants = validateConstants(ctx2);
    }

    if (toValidate & Source.Mapping) {
      mapping = validateMapping(ctx2);
    }
  }

  const hasAnyError = constants.length || mapping.length || general.length;
  const result = { hasAnyError, constants, mapping, general, isActiveField, firstIdFieldId };

  return result;
}

function validateFieldUsage(ctx, field) {
  const { usedFields, isActiveField } = ctx;
  const { id, fieldData, type : source } = field;
  if (!fieldData) {
    return { message: `Field cannot be empty`, sourceId: id, type: ErrorType.FieldIsEmpty };
  }

  const { name } = fieldData;
  const useData = usedFields.checkUsage({ id, name }, isActiveField);

  if (useData) {
    const { type, excelHeader } = useData;
    const source = type === Source.Mapping
        ? `mapped to column '${excelHeader.name}'`
        : `used by another constant`;
    const message = `Field '${name}' is already ${source}`
    return { message, sourceId: id, type: ErrorType.FieldIsReused, useData };
  }

  return null;
}

function validateMapping(ctx) {
  const { mapping, flowstepType, excelHeaders, originalHeadersCount, isActiveField } = ctx;
  const { externalIdFieldName, mappedHeaders } = mapping;
  const errors = [];
  
  // Special handling for case when mapping was not generated at all (for Convert Leads for instance)
  if (excelHeaders.length !== originalHeadersCount && originalHeadersCount) {
    errors.push({
      message: `Change in data structure detected. There were ${originalHeadersCount} data columns when you created this flow step, but now there are ${excelHeaders.length}. Please revisit your mappings.`,
      type: ErrorType.HeadersCountMissmatch,
    });
  }

  for (const header of mappedHeaders) {
    if (!isActiveField(header) || !header.isUsed) { continue; }

    const error = validateFieldUsage(ctx, header);
    error && errors.push(error);
  }

  if (flowstepType === Type.Upsert) {
    const message = !externalIdFieldName
      ? `External Id field must be selected`
      : !mappedHeaders.find(x => x.isUsed && x.fieldData?.name === externalIdFieldName)
        ? `External Id column must have an assigned field`
        : '';
    message && errors.push({ message, type: ErrorType.InvalidUpsert })
  }

  return errors;
}

function validateConstants(ctx) {
  const { isActiveField } = ctx;

  const errors = [];
  for (const c of ctx.mapping.constants) {
    if (!isActiveField(c)) { continue; }

    const error = validateFieldUsage(ctx, c);
    error && errors.push(error);

    const { valueError, id } = c;
    valueError && errors.push({ message: valueError, type: ErrorType.ValueError, sourceId: id });
  }
  return errors;
}
